"use client";
import { setVehicleSearchOwner } from "@/lib/vehicle/corporateSearch";

import { signOut } from "next-auth/react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdownMenu";
import {
  LogOut,
  Settings,
  Gift,
  Share2,
  ArrowRight,
  HelpCircle,
  Info,
  Menu,
  Zap,
  CircleCheck,
  Sparkles,
} from "lucide-react";
import { Session } from "next-auth";
import { usePostHog } from "posthog-js/react";

import { useMainLayout } from "@/app/context/mainLayoutProvider";
import { useGlobalStore } from "@/app/context/globalStoreProvider";
import { useRouter } from "next-nprogress-bar";
import { REFERRAL_CREDIT_GOAL } from "@/lib/referral";
import { cn, navItems } from "@/lib/utils";
import { useEffect, useMemo, useState, type CSSProperties } from "react";
import {
  interpolateHslTokens,
  parseHslToken,
  type HslTriple,
} from "@/lib/color";
import { Button, buttonVariants } from "@/components/ui/button";
import { Small } from "@/components/ui/typography";
import { GoogleAuthModal } from "@/components/modal/googleAuthModal";
import { usePathname, useSearchParams } from "next/navigation";
import { useUI } from "@/app/context/UIProvider";
import { UserStatus } from "@/generated/email_parser.openapi";
import useStatusChallenge from "@/hooks/client/useStatusChallenge";
import { resolveChallengeTarget } from "@/lib/statusChallengeEligibility";
import { linearGradientContainer } from "@/lib/statusChallengeStyles";
import { useProductAnnouncements } from "@/components/productAnnouncements/ProductAnnouncementsProvider";
import useLightSectionBehindHeader from "@/hooks/client/useLightSectionBehindHeader";
import { detectMobileOS, getAppStoreUrl, MobileOS } from "@/lib/appStoreLinks";
import GooglePlayLogo from "@/components/ui/svgComponents/GooglePlayLogo";
import AppleLogo from "@/components/ui/svgComponents/AppleLogo";

type Props = {
  user?: Session["user"];
  isLoggedIn?: boolean;
  /**
   * True while `useSession()` is still resolving — public pages fetch the
   * session client-side, so this window is one `/api/auth/session` round trip
   * (~150-250ms warm) on every `(public)` route.
   *
   * Only the Login branch is withheld during it. That branch is the one that
   * guesses at auth and gets it wrong, flashing "Login" at signed-in users.
   * The deals/suites CTAs are auth-agnostic — both just route to /onboarding —
   * so gating them too would put the landing pages' primary conversion
   * affordance behind a network round trip for no correctness gain.
   *
   * Required, not optional: a caller that forgets it silently reinstates the
   * flash, and `tsc` should catch that.
   */
  isAuthPending: boolean;
};

// Silver "Status Challenge" header CTA surface — the shared metallic slate fill
// + silver border tokens, matching the challenge flow's silver pills.
const STATUS_CHALLENGE_CTA_STYLE: CSSProperties = {
  borderColor: "hsl(var(--silver-border))",
  backgroundImage: "var(--gradient-silver-fill)",
};

const RightContainer: React.FC<Props> = ({ isLoggedIn, isAuthPending }) => {
  const pathname = usePathname();
  const posthog = usePostHog(); // Use the PostHog hook
  const router = useRouter();
  const { isWebView, isDealsPage, isSuitesPage, renderMiddleContent } =
    useMainLayout();
  const { isLessThanMd } = useUI();
  const lightSectionProgress = useLightSectionBehindHeader();
  const [loginColorTokens, setLoginColorTokens] = useState<{
    white: HslTriple;
    navy: HslTriple;
  } | null>(null);
  const [collapseMenu, setCollapseMenu] = useState(false);
  const [googleAuthModalOpen, setGoogleAuthModalOpen] = useState(false);
  // Resolved client-side only — `navigator` isn't available during SSR, and
  // guessing an OS server-side would render the wrong store logo in the
  // initial HTML and swap it after hydration.
  const [appStoreOs, setAppStoreOs] = useState<MobileOS | null>(null);
  useEffect(() => {
    setAppStoreOs(detectMobileOS(navigator.userAgent));
  }, []);
  const {
    hasUnread: hasUnreadAnnouncements,
    openLatest: openLatestAnnouncement,
  } = useProductAnnouncements();

  const { user: userData } = useGlobalStore();
  const {
    statusChallengeEligible,
    statusChallengeComplete,
    activeChallengeLoading,
    isSilverOnly,
    nightsCurrent,
    nightsRequiredSilver,
    spendingRequiredSilver,
    nightsRequiredGold,
    spendingRequiredGold,
  } = useStatusChallenge();

  // Header CTA: before enrollment it's just "Status Challenge"; once active it
  // counts down "{nights remaining} nights to {tier}" (Gold once Silver is met
  // on a dual-tier challenge).
  const isChallengeActive = !!userData?.data.activeStatusChallenge;
  const isChallengeComplete =
    isChallengeActive &&
    !activeChallengeLoading &&
    (statusChallengeComplete === "gold" ||
      (isSilverOnly && statusChallengeComplete === "silver"));
  const challengeCtaLabel = useMemo(() => {
    // Until the active-challenge API resolves, the hook returns legacy fallback
    // thresholds — show the neutral label rather than a wrong countdown.
    if (!isChallengeActive || activeChallengeLoading) return "Status Challenge";
    if (isChallengeComplete) return "Challenge complete";
    const { tier, nightsRemaining } = resolveChallengeTarget({
      isActive: true,
      isSilverOnly,
      statusChallengeComplete,
      nightsCurrent,
      nightsRequiredSilver,
      spendingRequiredSilver,
      nightsRequiredGold,
      spendingRequiredGold,
    });
    return `${nightsRemaining} night${nightsRemaining === 1 ? "" : "s"} to ${tier}`;
  }, [
    isChallengeComplete,
    isChallengeActive,
    activeChallengeLoading,
    isSilverOnly,
    statusChallengeComplete,
    nightsCurrent,
    nightsRequiredSilver,
    spendingRequiredSilver,
    nightsRequiredGold,
    spendingRequiredGold,
  ]);

  const _isLoggedIn =
    isLoggedIn || userData?.status === UserStatus.SiteAccessFull;

  // On (public) pages, GlobalStoreProvider is absent so userData is undefined.
  // Fall back to the session-based isLoggedIn prop to show full menu items.
  const hasFullAccess =
    userData?.status === UserStatus.EmailSynced ||
    userData?.status === UserStatus.SiteAccessFull ||
    (!userData && isLoggedIn);

  const searchParams = useSearchParams();
  const showRoomRateMonitor =
    searchParams.get("showRoomRateMonitor") === "true";

  useEffect(() => {
    setCollapseMenu(Boolean(renderMiddleContent));
  }, [renderMiddleContent]);

  // Read the two design tokens the Login text fades between once — they're
  // static CSS custom properties, not something that changes at runtime.
  useEffect(() => {
    const rootStyles = getComputedStyle(document.documentElement);
    const white = parseHslToken(rootStyles.getPropertyValue("--pure-white"));
    const navy = parseHslToken(
      rootStyles.getPropertyValue("--gondola-blue-950")
    );
    if (white && navy) {
      setLoginColorTokens({ white, navy });
    }
  }, []);

  // Continuous white -> true navy fade tracking `lightSectionProgress`, rather
  // than a binary class swap — falls back to plain white (via `undefined`,
  // letting the `text-pure-white` class apply) until the tokens are read.
  const loginTextColor = loginColorTokens
    ? interpolateHslTokens(
        loginColorTokens.white,
        loginColorTokens.navy,
        lightSectionProgress
      )
    : undefined;
  // Hide the header throughout onboarding, except the "How it Works" preview
  // (?how-it-works=true) where the menu must stay reachable.
  if (
    pathname?.startsWith("/onboarding") &&
    searchParams.get("how-it-works") !== "true"
  )
    return null;

  const handleLogout = () => {
    // Reset PostHog user state
    if (posthog) {
      posthog.reset();
    }
    // Then sign out
    setVehicleSearchOwner(null);
    signOut({ callbackUrl: "/api/user/logout" });
  };

  const onHowItWorksClick = () => {
    router.push("/onboarding/begin?how-it-works=true");
  };

  const onGondolaRewardsClick = () => {
    router.push("/gondola-rewards");
  };

  const onReferralsClick = () => {
    router.push("/invite");
  };

  const onAccountInformationClick = () => {
    router.push("/profile/account-information");
  };

  const onSupportClick = () => {
    router.push("/contact-us");
  };

  // Show nav items (Search, Points, Trips, Explore) in dropdown when breakpoint is low OR when middle shows custom content (e.g. search in header on /hotel/search scroll)
  const showNavDropdown = _isLoggedIn && (collapseMenu || isLessThanMd);

  return (
    <div
      className={cn("relative flex items-center gap-2 md:justify-end lg:gap-4")}
    >
      {_isLoggedIn ? (
        <div className="flex min-w-fit items-center justify-center gap-3 lg:gap-8">
          {!renderMiddleContent &&
            (statusChallengeEligible ? (
              <div className="hidden lg:block">
                <Button
                  className={cn(
                    "group relative h-12 overflow-hidden whitespace-nowrap rounded-lg border px-4 transition-colors duration-150",
                    isChallengeComplete
                      ? "border-success/30 bg-success/10 hover:bg-success/20"
                      : "hover:shadow-lg"
                  )}
                  style={
                    isChallengeComplete ? undefined : STATUS_CHALLENGE_CTA_STYLE
                  }
                  onClick={() => router.push("/challenge")}
                >
                  <span className="relative z-10 flex items-center gap-2 text-pure-white">
                    {isChallengeComplete ? (
                      <CircleCheck
                        className="h-4 w-4 text-success"
                        aria-hidden
                      />
                    ) : (
                      <Zap className="h-4 w-4" aria-hidden />
                    )}
                    <Small
                      className={cn(
                        "font-medium",
                        isChallengeComplete ? "text-success" : "text-pure-white"
                      )}
                    >
                      {challengeCtaLabel}
                    </Small>
                  </span>
                </Button>
              </div>
            ) : (
              <div className="hidden lg:block">
                <Button
                  className="hover:shadow-primary-gold/20 group relative overflow-hidden whitespace-nowrap px-6 py-3 transition-all duration-300 hover:scale-105 hover:shadow-lg"
                  style={linearGradientContainer}
                  onClick={() => router.push("/invite")}
                >
                  <span className="relative z-10 flex items-center gap-2">
                    <Gift className="h-4 w-4" aria-hidden />
                    <Small className="font-semibold">{`Earn $${REFERRAL_CREDIT_GOAL.toLocaleString()}`}</Small>
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-foreground/20 to-transparent opacity-0 transition-opacity duration-500 group-hover:translate-x-full group-hover:opacity-100" />
                </Button>
              </div>
            ))}
          <DropdownMenu>
            <DropdownMenuTrigger className="outline-none">
              <div className="group flex h-10 w-10 items-center justify-center rounded transition-colors hover:bg-foreground/10">
                <Menu className="h-6 w-6 text-muted-foreground group-hover:text-primary" />
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="flex w-[227px] min-w-0 flex-col rounded-lg border-0 bg-secondary py-6 shadow-md outline outline-1 outline-offset-[-1px] outline-foreground/10">
              {/* Mobile: main screens first */}
              {showNavDropdown && hasFullAccess && !showRoomRateMonitor && (
                <>
                  {navItems
                    .filter(
                      (item) =>
                        !("featureFlag" in item) ||
                        posthog?.isFeatureEnabled(
                          (item as { featureFlag: string }).featureFlag
                        )
                    )
                    .map((item) => (
                      <DropdownMenuItem
                        key={item.key}
                        onClick={() => router.push(item.href)}
                        className="h-8 min-h-8 cursor-pointer select-none gap-3 rounded-none bg-transparent px-6 py-2 text-sm font-medium leading-none text-foreground outline-none transition-colors hover:bg-foreground/10 focus:bg-foreground/10 focus:outline-none data-[disabled]:pointer-events-none data-[highlighted]:bg-foreground/10 data-[disabled]:opacity-50"
                      >
                        <span className="-mr-2 shrink-0 opacity-50">
                          {item.icon}
                        </span>
                        <span className="flex min-w-0 items-center gap-2">
                          <span>{item.title}</span>
                        </span>
                      </DropdownMenuItem>
                    ))}
                  <div className="w-full px-6 py-4">
                    <div
                      className="w-full shrink-0 border-t border-foreground/20"
                      role="separator"
                    />
                  </div>
                </>
              )}
              {/* 1st: Gondola Rewards and the referral CTA */}
              {hasFullAccess && !showRoomRateMonitor && (
                <>
                  <DropdownMenuItem
                    onClick={onGondolaRewardsClick}
                    className="h-8 min-h-8 cursor-pointer select-none gap-3 rounded-none bg-transparent px-6 py-2 text-sm font-medium leading-none text-foreground outline-none transition-colors hover:bg-foreground/10 focus:bg-foreground/10 focus:outline-none data-[disabled]:pointer-events-none data-[highlighted]:bg-foreground/10 data-[disabled]:opacity-50"
                  >
                    <Gift className="h-3.5 w-3.5 shrink-0 opacity-50" />
                    Gondola Rewards
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={onReferralsClick}
                    className="h-8 min-h-8 cursor-pointer select-none gap-3 rounded-none bg-transparent px-6 py-2 text-sm font-medium leading-none text-foreground outline-none transition-colors hover:bg-foreground/10 focus:bg-foreground/10 focus:outline-none data-[disabled]:pointer-events-none data-[highlighted]:bg-foreground/10 data-[disabled]:opacity-50"
                  >
                    <Share2 className="h-3.5 w-3.5 shrink-0 opacity-50" />
                    {`Earn $${REFERRAL_CREDIT_GOAL.toLocaleString()}`}
                  </DropdownMenuItem>
                  <div className="w-full px-6 py-4">
                    <div
                      className="w-full shrink-0 border-t border-foreground/20"
                      role="separator"
                    />
                  </div>
                  {/* 2nd: Account Information */}
                  <DropdownMenuItem
                    onClick={onAccountInformationClick}
                    className="h-8 min-h-8 cursor-pointer select-none gap-3 rounded-none bg-transparent px-6 py-2 text-sm font-medium leading-none text-foreground outline-none transition-colors hover:bg-foreground/10 focus:bg-foreground/10 focus:outline-none data-[disabled]:pointer-events-none data-[highlighted]:bg-foreground/10 data-[disabled]:opacity-50"
                  >
                    <Settings className="h-3.5 w-3.5 shrink-0 opacity-50" />
                    Account Information
                  </DropdownMenuItem>
                  <div className="w-full px-6 py-4">
                    <div
                      className="w-full shrink-0 border-t border-foreground/20"
                      role="separator"
                    />
                  </div>
                  {/* 3rd: Support, How it Works */}
                  <DropdownMenuItem
                    onClick={onSupportClick}
                    className="h-8 min-h-8 cursor-pointer select-none gap-3 rounded-none bg-transparent px-6 py-2 text-sm font-medium leading-none text-foreground outline-none transition-colors hover:bg-foreground/10 focus:bg-foreground/10 focus:outline-none data-[disabled]:pointer-events-none data-[highlighted]:bg-foreground/10 data-[disabled]:opacity-50"
                  >
                    <HelpCircle className="h-3.5 w-3.5 shrink-0 opacity-50" />
                    Support
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => openLatestAnnouncement("account_menu")}
                    className="h-8 min-h-8 cursor-pointer select-none gap-3 rounded-none bg-transparent px-6 py-2 text-sm font-medium leading-none text-foreground outline-none transition-colors hover:bg-foreground/10 focus:bg-foreground/10 focus:outline-none data-[disabled]:pointer-events-none data-[highlighted]:bg-foreground/10 data-[disabled]:opacity-50"
                  >
                    <span className="relative">
                      <Sparkles className="h-3.5 w-3.5 shrink-0 opacity-50" />
                      {hasUnreadAnnouncements ? (
                        <span className="absolute -right-1 -top-1 size-2 rounded-full bg-primary" />
                      ) : null}
                    </span>
                    What&apos;s New
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={onHowItWorksClick}
                    className="h-8 min-h-8 cursor-pointer select-none gap-3 rounded-none bg-transparent px-6 py-2 text-sm font-medium leading-none text-foreground outline-none transition-colors hover:bg-foreground/10 focus:bg-foreground/10 focus:outline-none data-[disabled]:pointer-events-none data-[highlighted]:bg-foreground/10 data-[disabled]:opacity-50"
                  >
                    <Info className="h-3.5 w-3.5 shrink-0 opacity-50" />
                    How it Works
                  </DropdownMenuItem>
                  <div className="w-full px-6 py-4">
                    <div
                      className="w-full shrink-0 border-t border-foreground/20"
                      role="separator"
                    />
                  </div>
                </>
              )}
              {/* 4th: Log Out */}
              <DropdownMenuItem
                onClick={handleLogout}
                className="h-8 min-h-8 cursor-pointer select-none gap-3 rounded-none bg-transparent px-6 py-2 text-sm font-medium leading-none text-foreground outline-none transition-colors hover:bg-foreground/10 focus:bg-foreground/10 focus:outline-none data-[disabled]:pointer-events-none data-[highlighted]:bg-foreground/10 data-[disabled]:opacity-50"
              >
                <LogOut className="h-3.5 w-3.5 shrink-0 opacity-50" />
                Log Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ) : isSuitesPage ? (
        <Button
          variant="orange-gradient"
          className="h-10 whitespace-nowrap text-xs sm:text-base lg:h-12"
          onClick={() => router.push("/onboarding")}
        >
          Search all suites
          <ArrowRight className="w-4 lg:w-6" />
        </Button>
      ) : isDealsPage ? (
        <Button
          className="h-10 whitespace-nowrap bg-brand-gold text-xs sm:text-base lg:h-12"
          onClick={() => router.push("/onboarding")}
        >
          Get Started
          <ArrowRight className="w-4 lg:w-6" />
        </Button>
      ) : isAuthPending ? null : ( // Auth unresolved — see `isAuthPending`.
        <>
          {/* Not gated by `isWebView` — unlike Login below, this doesn't
              depend on Google's OAuth popup (which doesn't work inside most
              in-app browsers), it just opens an external store link. Inside
              an in-app browser like Instagram's, this is the only download
              affordance in the header at all, since Login stays hidden
              there — gating it the same way made it vanish entirely on
              those pages, not just fade out.

              A real anchor, not a `Button` + `window.open()` click handler:
              some in-app browsers (Instagram's included) sandbox or
              silently no-op script-triggered `window.open` calls, and now
              that this renders inside them, it's the one surface where a
              webview visitor would have no other way to reach the store —
              the same reliability issue already found and fixed on the
              header's mobile notification pill before it was reverted to
              this plain button. `buttonVariants` reproduces the `Button`
              styling on a plain `<a>`, matching how external links elsewhere
              in this codebase (BannerSlider, McpSection) borrow it. */}
          <a
            href={appStoreOs ? getAppStoreUrl(appStoreOs) : undefined}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              buttonVariants({ variant: "outline" }),
              "shrink-0 rounded-full px-3 text-xs sm:px-6 sm:text-base",
              !loginTextColor && "text-pure-white"
            )}
            style={loginTextColor ? { color: loginTextColor } : undefined}
            onClick={() => {
              if (!appStoreOs) return;
              posthog?.capture("header_download_app_clicked", {
                os: appStoreOs,
              });
            }}
          >
            {/* `appStoreOs` only resolves client-side (see its declaration)
                — `navigator` isn't available during SSR, and guessing
                would render the wrong logo (and store link) in the initial
                HTML and swap it after hydration. No icon/href for the one
                render before it resolves is a much smaller flash than a
                wrong one. */}
            {appStoreOs === "android" ? (
              <GooglePlayLogo className="h-4 w-4" aria-hidden />
            ) : appStoreOs === "ios" ? (
              <AppleLogo className="h-4 w-4" aria-hidden />
            ) : null}
            Download app
          </a>
          {!isWebView && (
            <>
              <GoogleAuthModal
                headerText="Login"
                open={googleAuthModalOpen}
                onOpenChange={setGoogleAuthModalOpen}
              />
              <Button
                variant="outline"
                // The header floats over the video, dark sections, and the
                // light "Travel like a pro" section as the page scrolls.
                // `useLightSectionBehindHeader` reports how much of that light
                // section (marked with `data-header-invert`) is behind the
                // fixed header as a 0-1 fraction, and `loginTextColor` fades
                // continuously between white and true navy as it changes,
                // rather than snapping at a threshold. `border-input` (from
                // the `outline` variant) already reads fine against both, so
                // it's left alone.
                className={cn(
                  "shrink-0 rounded-full px-3 text-xs sm:px-6 sm:text-base",
                  !loginTextColor && "text-pure-white"
                )}
                style={loginTextColor ? { color: loginTextColor } : undefined}
                onClick={() => setGoogleAuthModalOpen(true)}
              >
                Login
              </Button>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default RightContainer;
